import { useNavigate, useSearch } from "@tanstack/react-router";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import {
  useOrphanAccounts,
  useOrphanContracts,
  useOrphanPosNoCompany,
  useOrphanPosNoMaster,
} from "../api";
import type { RenewalsDataSearch } from "..";
import { RenewalsSubNav } from "../components/SubNav";

type Tab = "contracts" | "accounts" | "pos-no-master" | "pos-no-company";

const TABS: { key: Tab; label: string; description: string }[] = [
  {
    key: "contracts",
    label: "Orphan contracts",
    description: "Contracts whose account row is missing or has no QuickBooks ID.",
  },
  {
    key: "accounts",
    label: "Orphan accounts",
    description: "Accounts with a QB ID but no contracts attached.",
  },
  {
    key: "pos-no-master",
    label: "POs w/o master",
    description: "Purchase Order contracts on accounts that have no master agreement.",
  },
  {
    key: "pos-no-company",
    label: "POs w/o matching company",
    description: "PO company_name doesn't match any company on the account.",
  },
];

function DataTable({ rows }: { rows: Record<string, unknown>[] }) {
  const first = rows[0];
  if (!first) {
    return <p className="px-4 py-3 text-sm text-muted-foreground">No rows.</p>;
  }
  const cols = Object.keys(first);
  return (
    <Table containerClassName="max-h-[calc(100vh-22rem)]">
      <TableHeader>
        <TableRow>
          {cols.map((c) => (
            <TableHead key={c} className="whitespace-nowrap">
              {c}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody zebra>
        {rows.map((row, i) => (
          <TableRow key={i}>
            {cols.map((c) => (
              <TableCell key={c} className="whitespace-nowrap">
                {String(row[c] ?? "")}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function DataPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/renewals/data" }) as RenewalsDataSearch;
  // Tab lives in the URL (?tab=…) — a plain push (no `replace`) so the browser
  // back button steps back to the tab you were previously on.
  const tab: Tab = search.tab ?? "contracts";
  const setTab = (next: Tab) =>
    navigate({
      to: "/renewals/data",
      search: (prev: RenewalsDataSearch) => ({ ...prev, tab: next }),
    });
  const contracts = useOrphanContracts();
  const accounts = useOrphanAccounts();
  const posNoMaster = useOrphanPosNoMaster();
  const posNoCompany = useOrphanPosNoCompany();

  const active = TABS.find((t) => t.key === tab)!;
  const q = (
    tab === "contracts" ? contracts
    : tab === "accounts" ? accounts
    : tab === "pos-no-master" ? posNoMaster
    : posNoCompany
  );

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Renewals — Data</h1>
        <p className="text-sm text-muted-foreground">
          Data-quality reports for accounts and contracts that don't roll into the live pipeline.
        </p>
      </div>
      <RenewalsSubNav />

      <div className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cn(
              "rounded-t-md border-b-2 px-3 py-1.5 text-sm font-medium",
              tab === t.key
                ? "border-brand text-brand"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
            {/* live count badges from the response */}
            {(t.key === "contracts" && contracts.data?.count != null) && (
              <span className="ml-1 rounded-full bg-muted px-1.5 text-xs">
                {contracts.data.count}
              </span>
            )}
            {(t.key === "accounts" && accounts.data?.count != null) && (
              <span className="ml-1 rounded-full bg-muted px-1.5 text-xs">
                {accounts.data.count}
              </span>
            )}
            {(t.key === "pos-no-master" && posNoMaster.data?.count != null) && (
              <span className="ml-1 rounded-full bg-muted px-1.5 text-xs">
                {posNoMaster.data.count}
              </span>
            )}
            {(t.key === "pos-no-company" && posNoCompany.data?.count != null) && (
              <span className="ml-1 rounded-full bg-muted px-1.5 text-xs">
                {posNoCompany.data.count}
              </span>
            )}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{active.label}</CardTitle>
          <CardDescription>{active.description}</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {q.isLoading && <p className="px-4 py-3 text-sm text-muted-foreground">Loading…</p>}
          {q.error && (
            <p className="px-4 py-3 text-sm text-destructive">{String(q.error)}</p>
          )}
          {q.data && <DataTable rows={q.data.rows ?? []} />}
        </CardContent>
      </Card>
    </div>
  );
}
